<template>
  <div
    class="rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
    :style="containerStyle"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

interface Props {
  symbol?: string
  address?: string
  logoURI?: string
  size?: number // size in pixels
}

const props = defineProps<Props>()
const imageError = ref(false)

watch(
  () => [props.symbol, props.address, props.logoURI],
  () => {
    imageError.value = false
  }
)

const logoUrl = computed(() => {
  if (imageError.value) return null

  if (props.logoURI) return props.logoURI

  const symbol = props.symbol?.toUpperCase()
  const address = props.address?.toLowerCase()

  if (
    address === '0x0000000000000000000000000000000000000000' ||
    address === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' ||
    symbol === 'ETH'
  ) {
    return 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/info/logo.png'
  }

  const commonTokens: Record<string, string> = {
    USDT: 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/0xdAC17F958D2ee523a2206206994597C13D831ec7/logo.png',
    USDC: 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/logo.png',
    DAI: 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/0x6B175474E89094C44Da98b954EedeAC495271d0F/logo.png',
    WBTC: 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599/logo.png',
    SIS: 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/0xd38BB40815d2B0c2d2c866e0c72c5728ffC76dd9/logo.png',
    TON: 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ton/info/logo.png',
  }

  if (symbol && commonTokens[symbol]) return commonTokens[symbol]

  return null
})

const fallbackColors = [
  '#627EEA',
  '#FF0420',
  '#0052FF',
  '#12AAFF',
  '#F7931A',
  '#2775CA',
  '#26A17B',
  '#8247E5',
  '#FF6B6B',
  '#4ECDC4',
]

const fallbackColor = computed(() => {
  const index = props.symbol ? props.symbol.charCodeAt(0) % fallbackColors.length : 0
  return fallbackColors[index]
})

const containerStyle = computed(() => {
  const hasImage = logoUrl.value && !imageError.value
  const size = props.size ?? 24 // default 24px
  return {
    width: `${String(size)}px`,
    height: `${String(size)}px`,
    backgroundColor: hasImage ? 'transparent' : fallbackColor.value,
    backgroundImage: hasImage ? 'url(' + logoUrl.value + ')' : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  }
})
</script>
